import {
  isNotNull,
  OnboardingStepType,
  OrganizationDetailFragment,
  useStartOnboardingStep,
} from '@alice-financial/alice-sdk'
import {
  alpha,
  Box,
  Card,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@alice-financial/pretext-ui'
import * as React from 'react'
import { useOrgQuery } from '../../organization/useOrg'
import { useSetUnifiedFunding } from '../funding/useSetUnifiedFunding'

// Utility functions similar to billingUtils
const determineCanUsePaygroupFunding = (org?: OrganizationDetailFragment | null) =>
  Boolean(org && (hasStartedFundingConnection(org) || !hasFundingConnected(org)))

const hasFundingConnected = (fundable: {
  aliceCardFunding: { availablePaymentMethods?: unknown[] | null }
}) => Boolean((fundable.aliceCardFunding.availablePaymentMethods || []).length > 0)

const hasStartedFundingConnection = (org: OrganizationDetailFragment) =>
  hasFundingConnected(org) || org.employers.filter(isNotNull).some(hasFundingConnected)

export const UnifiedFundingForm = ({ org }: { org: OrganizationDetailFragment }) => {
  const { isFetching: isRefreshingOrg } = useOrgQuery()
  const { mutate: setUnifiedFunding, isLoading: isSaving } = useSetUnifiedFunding()
  const { mutate: startOnboardingStep } = useStartOnboardingStep(org.id)
  const isUpdatingOrg = isSaving || isRefreshingOrg

  const canUsePaygroupFunding = determineCanUsePaygroupFunding(org)

  if (!org || !canUsePaygroupFunding) return null
  if (hasStartedFundingConnection(org)) {
    if (org.aliceCardProgram.hasUnifiedBanking) {
      return (
        <Typography variant="body2" gutterBottom>
          You&apos;ve chosen to fund all pay groups from the same account
        </Typography>
      )
    }
    return (
      <Typography variant="body2" gutterBottom>
        You&apos;ve chosen to connect a funding account for <strong>each pay group</strong>.
      </Typography>
    )
  }

  const handleChange = (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    // Start CONNECT_BANKING step when unified/non-unified banking is selected
    startOnboardingStep({ stepType: OnboardingStepType.ConnectBanking })
    setUnifiedFunding({ hasUnifiedBanking: value === 'true' })
  }

  const currentUnifiedFundingValue = org.aliceCardProgram.hasUnifiedBanking
  const disabled = isUpdatingOrg

  return (
    <>
      <Typography variant="body2" gutterBottom>
        We support funding at the <strong>organization</strong> level or <strong>per-pay-group</strong>.
      </Typography>

      <Box mb={2}>
        <Grid
          container
          spacing={2}
          component={RadioGroup}
          value={currentUnifiedFundingValue}
          onChange={handleChange}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              component={FormControlLabel}
              disabled={disabled}
              value={true}
              control={<Radio />}
              label="All employee contributions will be funded from one account"
              sx={(theme) => ({
                p: theme.spacing(1, 1, 1.8),
                m: 0,
                backgroundColor: currentUnifiedFundingValue
                  ? alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity)
                  : undefined,
                borderColor: currentUnifiedFundingValue ? theme.palette.primary.main : undefined,
              })}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              component={FormControlLabel}
              disabled={disabled}
              value={false}
              control={<Radio />}
              label="Employee contributions from each pay group will be funded separately"
              sx={(theme) => ({
                p: theme.spacing(1, 1, 1.8),
                m: 0,
                backgroundColor: currentUnifiedFundingValue
                  ? undefined
                  : alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
                borderColor: currentUnifiedFundingValue ? undefined : theme.palette.primary.main,
              })}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}
